import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Check, X, Loader2, Upload, Globe, Palette, Info } from 'lucide-react';

/**
 * Branding — the partner's own subdomain, logo and colours.
 *
 * Two halves, and the order matters. The subdomain is claimed once and is then permanent in
 * practice: their clients bookmark it, so changing it later breaks every one of them. The
 * logo and colours can change whenever they like.
 *
 * What is deliberately NOT on this page: the tier, and the "Powered by Expedify" line. Both
 * are Expedify's to grant. A partner arriving here before a tier has been granted is told so
 * plainly rather than shown a form that would fail on save.
 */
const LOGO_KINDS = [
  { kind: 'light', label: 'Logo (light backgrounds)', hint: 'Shown on white. PNG or SVG, wide.' },
  { kind: 'dark', label: 'Logo (dark backgrounds)', hint: 'Shown in dark mode. Falls back to the light one.' },
  // Named for where it actually shows up. "Icon" was accurate and told a partner nothing:
  // this is the image their client sees in the browser tab and keeps in a bookmark, which is
  // the one they most want to be theirs and the one they are least likely to think of.
  { kind: 'mark', label: 'Favicon / app icon', hint: 'Square, at least 64x64. Browser tab, bookmarks, and the collapsed menu. Until you upload one we use your initial on your brand colour.' },
];

const PartnerBranding = () => {
  const { getAuthHeaders, API_URL } = useAuth();

  const [brand, setBrand] = useState(null);
  const [logoUrls, setLogoUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [slug, setSlug] = useState('');
  const [slugState, setSlugState] = useState(null);   // {available, hostname, reason, suggestions}
  const [checking, setChecking] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const debounce = useRef(null);

  const [form, setForm] = useState({
    app_name: '', primary_color: '#6366F1', secondary_color: '#8B5CF6',
    support_email: '', support_url: '',
  });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/partner/branding`, { headers: getAuthHeaders() });
      setBrand(res.data);
      if (res.data?.granted) {
        setForm((f) => ({
          ...f,
          app_name: res.data.app_name || '',
          primary_color: res.data.primary_color || f.primary_color,
          secondary_color: res.data.secondary_color || f.secondary_color,
          support_email: res.data.support_email || '',
          support_url: res.data.support_url || '',
        }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load your branding');
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  // Debounced so a partner typing "northwind" asks once, not nine times.
  useEffect(() => {
    if (!slug.trim()) { setSlugState(null); return; }
    clearTimeout(debounce.current);
    setChecking(true);
    debounce.current = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/partner/branding/check-slug?slug=${encodeURIComponent(slug.trim())}`,
          { headers: getAuthHeaders() },
        );
        setSlugState(res.data);
      } catch {
        setSlugState(null);
      } finally {
        setChecking(false);
      }
    }, 400);
    return () => clearTimeout(debounce.current);
  }, [slug, API_URL, getAuthHeaders]);

  const claim = async () => {
    setClaiming(true);
    try {
      const res = await axios.post(`${API_URL}/api/partner/branding/claim-slug`,
        { slug: slug.trim() }, { headers: getAuthHeaders() });
      toast.success(`${res.data.hostname} is yours`);
      setSlug(''); setSlugState(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not claim that subdomain');
    } finally {
      setClaiming(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/partner/branding`, form, { headers: getAuthHeaders() });
      toast.success('Branding saved');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('file', file);
    try {
      await axios.post(`${API_URL}/api/partner/branding/logo`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Logo updated');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not upload that file');
    }
  };

  // Previews come through the portal's own proxy, because the platform returns a relative
  // path and the browser has no public URL for the platform.
  //
  // Fetched as a blob rather than set as <img src> directly: this portal authenticates with
  // a Bearer header, and an <img> tag cannot send one. The object URLs are revoked when they
  // are replaced, or the tab leaks a copy of every logo the partner tries.
  useEffect(() => {
    if (!brand?.granted) return;
    const paths = {
      light: brand.logo_light_path, dark: brand.logo_dark_path, mark: brand.mark_path,
    };
    const created = [];
    let cancelled = false;

    (async () => {
      const next = {};
      for (const [kind, path] of Object.entries(paths)) {
        if (!path || !path.startsWith('/api/public/branding/asset/')) continue; // still a default
        try {
          const res = await axios.get(`${API_URL}/api/partner/branding/logo/${kind}`, {
            headers: getAuthHeaders(), responseType: 'blob',
          });
          const url = URL.createObjectURL(res.data);
          created.push(url);
          next[kind] = url;
        } catch { /* a preview that will not load is not worth an error toast */ }
      }
      if (cancelled) { created.forEach(URL.revokeObjectURL); return; }
      setLogoUrls(next);
    })();

    return () => { cancelled = true; created.forEach(URL.revokeObjectURL); };
  }, [brand, API_URL, getAuthHeaders]);

  const logoSrc = (kind) => logoUrls[kind] || null;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading
        </div>
      </DashboardLayout>
    );
  }

  if (!brand?.granted) {
    return (
      <DashboardLayout>
        <div className="p-8 max-w-2xl">
          <h1 className="text-2xl font-bold mb-2">Branding</h1>
          <Card>
            <CardContent className="p-6 flex gap-3">
              <Info className="h-5 w-5 flex-shrink-0 mt-0.5 text-muted-foreground" />
              <div className="text-sm">
                <p className="font-medium">Branding is not enabled on your account yet.</p>
                <p className="text-muted-foreground mt-1">
                  It lets you run Expedify on your own subdomain, with your logo and colours,
                  for the clients you bill. Talk to your partnership manager to switch it on —
                  they enable it, then everything else on this page is yours to set.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const primaryHost = brand.hostnames?.find((h) => h.is_primary)?.hostname;

  return (
    <DashboardLayout>
      <div className="p-8 max-w-3xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Branding</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Your clients see this, not Expedify's. A small “Powered by Expedify” stays in the
            footer.
          </p>
        </div>

        {/* Subdomain */}
        <Card>
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center gap-2 font-medium"><Globe className="h-4 w-4" /> Your address</div>

            {primaryHost ? (
              <div>
                <p className="font-mono text-sm">https://{primaryHost}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Your clients bookmark this, so it does not change. Ask us if you need it moved.
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Pick the address your clients will use. Choose carefully — once your clients
                  start using it, changing it breaks their bookmarks.
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.toLowerCase())}
                    placeholder="yourcompany"
                    className="max-w-[220px] font-mono"
                  />
                  <span className="text-sm text-muted-foreground font-mono">.expedify.ai</span>
                  {checking && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  {!checking && slugState?.available && <Check className="h-4 w-4 text-emerald-600" />}
                  {!checking && slugState && !slugState.available && <X className="h-4 w-4 text-red-600" />}
                </div>

                {slugState?.reason && (
                  <p className={`text-xs ${slugState.available ? 'text-muted-foreground' : 'text-red-600'}`}>
                    {slugState.reason}
                  </p>
                )}

                {!!slugState?.suggestions?.length && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground">Free:</span>
                    {slugState.suggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSlug(s)}
                        className="text-xs font-mono px-2 py-1 rounded border hover:bg-muted transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}

                <Button onClick={claim} disabled={!slugState?.available || claiming}>
                  {claiming && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Claim {slugState?.available ? slugState.hostname : 'this address'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Logos */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2 font-medium"><Upload className="h-4 w-4" /> Logos</div>
            {LOGO_KINDS.map(({ kind, label, hint }) => (
              <div key={kind} className="flex items-center gap-4">
                <div className="w-40 h-12 border rounded flex items-center justify-center bg-muted/30 overflow-hidden">
                  {logoSrc(kind)
                    ? <img src={logoSrc(kind)} alt={label} className="max-h-10 max-w-[150px] object-contain" />
                    : <span className="text-xs text-muted-foreground">none</span>}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{label}</p>
                  <p className="text-xs text-muted-foreground">{hint}</p>
                </div>
                <label className="text-sm px-3 py-1.5 border rounded cursor-pointer hover:bg-muted transition-colors">
                  Upload
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={(e) => uploadLogo(kind, e.target.files?.[0])}
                  />
                </label>
              </div>
            ))}
            <p className="text-xs text-muted-foreground">PNG, JPEG, WebP or SVG. Up to 512KB.</p>
          </CardContent>
        </Card>

        {/* Name, colours, support */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2 font-medium"><Palette className="h-4 w-4" /> Name and colours</div>

            <div>
              <label className="text-sm font-medium block mb-1">Product name</label>
              <Input
                value={form.app_name}
                onChange={(e) => setForm({ ...form, app_name: e.target.value })}
                placeholder="Northwind CRM"
                className="max-w-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">Shown in the menu and the browser tab.</p>
            </div>

            <div className="flex gap-6">
              {[['primary_color', 'Primary'], ['secondary_color', 'Secondary']].map(([key, label]) => (
                <div key={key}>
                  <label className="text-sm font-medium block mb-1">{label}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form[key]}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      className="h-9 w-12 rounded border cursor-pointer"
                    />
                    <Input
                      value={form[key]}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      className="w-28 font-mono text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium block mb-1">Support email</label>
                <Input
                  value={form.support_email}
                  onChange={(e) => setForm({ ...form, support_email: e.target.value })}
                  placeholder="help@yourcompany.com"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Support link</label>
                <Input
                  value={form.support_url}
                  onChange={(e) => setForm({ ...form, support_url: e.target.value })}
                  placeholder="https://yourcompany.com/help"
                />
              </div>
            </div>

            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PartnerBranding;
